import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { AbstractControl, ReactiveFormsModule, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService } from '../../../ui';
import { ContractsService } from '../contracts.service';
import { AuthService } from '../../../core/auth/auth.service';
import { PermissionService } from '../../../core/auth/permission.service';
import { SessionService } from '../../../core/session/session.service';
import { extensionDualName } from '../extension-name.util';
import { parseApiDate } from '../../../shared/utils/contract-change-date.util';
import {
  HLM_DIALOG_SHELL,
  HLM_FORM_FIELD,
  HlmAlertComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmNativeSelectDirective,
  HlmComboboxComponent,
  HlmInputDirective,
  HlmTextareaDirective,
  HlmSpinnerComponent,
} from '../../../ui';

/** Server module that gates granting a permanent (no-endDate) discount. CMB-2953. */
const PERMANENT_DISCOUNT_MODULE = 'contract.permanentDiscount.grant';

/** Requires a non-blank reason of at least `min` non-whitespace characters (whitespace-only is rejected). */
function nonBlankReason(min: number) {
  return (control: AbstractControl): ValidationErrors | null =>
    (control.value ?? '').trim().length >= min ? null : { reasonRequired: true };
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * True for a real calendar day written exactly as `yyyy-MM-dd` with a year from 1900. Rejects what the
 * native date input can still emit and `parseApiDate` alone lets through: a 5-digit year (`20260-01-10`),
 * years 0001-0099 (`new Date(y, …)` maps them to 19xx, so hint and booking would be ~1900 years off)
 * and rolled-over days (`2026-02-31` becomes 3 March).
 */
function isRealIsoDay(value: string): boolean {
  const match = ISO_DAY.exec(value);
  if (!match) return false;
  const [year, month, day] = match.slice(1).map(Number);
  if (year < 1900) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/** Without a usable start date the end date cannot be computed and Apply would do nothing. */
function validStartDate(control: AbstractControl): ValidationErrors | null {
  return !control.value || isRealIsoDay(control.value) ? null : { invalidDate: true };
}

/**
 * Default discount start date = `invoiceDay + 1` of the current or next month (the next billing
 * boundary), replicating old CMT `getDefaultStartDate`. Always today-or-later. A back-dated start
 * is still allowed (operators may grant a discount retroactively) — see the past-date warning.
 */
function defaultDiscountStartDate(invoiceDay: number): string {
  const d = new Date();
  const addMonth = d.getDate() > invoiceDay - 1;
  d.setDate(1); // set to the 1st before shifting the month, so setMonth can't overflow
  if (addMonth) d.setMonth(d.getMonth() + 1);
  d.setDate(invoiceDay + 1);
  return formatDate(d, 'yyyy-MM-dd', 'en');
}

/**
 * Add `months` to `date`, clamping to the last valid day of the target month (RB.4).
 * Plain `setMonth(+n)` overflows a month-end start (Jan 31 + 1mo → Mar 3); this keeps it Feb 28/29.
 */
function addMonths(date: Date, months: number): Date {
  const targetDay = date.getDate();
  const r = new Date(date);
  r.setDate(1);
  r.setMonth(r.getMonth() + months);
  const daysInTargetMonth = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
  r.setDate(Math.min(targetDay, daysInTargetMonth));
  return r;
}

/** `date` shifted by `days` calendar days. */
function addDays(date: Date, days: number): Date {
  const r = new Date(date);
  r.setDate(r.getDate() + days);
  return r;
}

/** End of the ISO week (Sunday) containing `date`; the same day if `date` is already a Sunday. */
function endOfWeek(date: Date): Date {
  const r = new Date(date);
  r.setDate(r.getDate() + ((7 - r.getDay()) % 7));
  return r;
}

/**
 * End of the billing period containing `date` = the `invoiceDay` (clamped to the month's last day
 * for short months), replicating old CMT `getDefaultEndDate` / `isInvoiceDay`. The period runs
 * [invoiceDay+1 … next invoiceDay], so the end is the first invoiceDay on/after the period start.
 */
function endOfBillingPeriod(date: Date, invoiceDay: number): Date {
  const r = new Date(date);
  if (r.getDate() > invoiceDay) {
    r.setDate(1);
    r.setMonth(r.getMonth() + 1);
  }
  const lastDay = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
  r.setDate(Math.min(invoiceDay, lastDay));
  return r;
}

interface BookedExtension {
  changeDate: string;
  startDate: string;
  endDate: string | null;
  changingEmployee: number | null;
  count: number;
  discount: number;
  extensionPriceInfo: { name: string; comment: string | null; labelKey: string; monthlyFee: number; status: string | null };
}

export interface ChangeDiscountDialogData {
  contractId: number;
  customerId: number;
  invoiceDay: number;
  mode: 'plan' | 'extension';
  monthlyFee?: number;
  currentPlanName: string;
  currency: string;
}

interface DurationOption {
  label: string;
  /** Permanent (no end date) — gated by the PERMANENT_DISCOUNT_MODULE; addFn unused. */
  permanent?: boolean;
  addFn?: (d: Date) => Date;
}

@Component({
  selector: 'cmt-change-discount-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL, ...HLM_FORM_FIELD,
    HlmAlertComponent, HlmBtnDirective, HlmIconComponent, HlmNativeSelectDirective, HlmComboboxComponent,
    HlmInputDirective, HlmTextareaDirective, HlmSpinnerComponent,
  ],
  template: `
    <hlm-dialog-surface size="md">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucidePercent" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>Change Discount ({{ data.mode === 'plan' ? 'Plan' : 'Extension' }})</h2>
          <p hlmDialogDescription>
            @if (data.mode === 'plan') {
              Plan: <strong>{{ data.currentPlanName }}</strong> ·
              <span class="font-mono-feat">{{ data.monthlyFee ?? 0 }} {{ data.currency }}/mo</span>
            } @else {
              Select an extension to apply discount
            }
          </p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        @if (loading()) {
          <div class="flex items-center gap-3 py-8 text-sm text-muted-foreground">
            <hlm-spinner size="18" />
            <span>Loading...</span>
          </div>
        } @else {
          <form [formGroup]="form" class="flex flex-col gap-4">
            @if (data.mode === 'extension') {
              <hlm-form-field>
                <label hlmLabel for="extension">Extension</label>
                <hlm-combobox
                  inputId="extension"
                  [options]="extensionOptions()"
                  placeholder="Select extension…"
                  formControlName="extensionIndex"
                />
              </hlm-form-field>
            }

            <hlm-form-field>
              <label hlmLabel for="startDate">Start date</label>
              <input id="startDate" hlmInput type="date" formControlName="startDate" />
              @if (form.controls['startDate'].hasError('invalidDate')) {
                <p hlmFieldError>Enter a valid start date.</p>
              }
            </hlm-form-field>

            <!-- Back-dating is allowed (retroactive discount); warn but don't block. -->
            @if (form.value.startDate && form.value.startDate < minDateStr) {
              <div class="-mt-2 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2">
                <hlm-icon name="lucideTriangleAlert" size="0.9rem" class="mt-0.5 flex-none text-warning" />
                <p class="text-sm text-foreground">
                  Start date is in the past — the discount will apply <strong>retroactively</strong>.
                </p>
              </div>
            }

            <hlm-form-field>
              <label hlmLabel for="duration">Duration</label>
              <select id="duration" hlmNativeSelect formControlName="durationIndex">
                <option [value]="null" disabled>Select duration...</option>
                @for (d of durations; track d.label; let i = $index) {
                  <option [value]="i">{{ d.label }}</option>
                }
              </select>
            </hlm-form-field>

            <!-- Shows what will actually be booked: the end date is inclusive (last discounted day).
                 Info tone (blue), not warn: this is the result the operator should check, not a problem —
                 the yellow warning blocks in this dialog stay reserved for actual warnings. -->
            @if (endDateHint(); as hint) {
              <hlm-alert tone="info" role="note" class="-mt-2" data-testid="end-date-hint">
                <hlm-icon name="lucideInfo" size="0.9rem" class="mt-0.5 flex-none text-primary" />
                <span>{{ hint }}</span>
              </hlm-alert>
            }

            <!-- Discoverability hint for operators without the permanent-discount module (CMB-2953).
                 Informational only — the option stays hidden and the server still gates the grant.
                 Uses the shared warning-block pattern (cc-numbers customer-visibility notice). -->
            @if (!canGrantPermanent) {
              <div class="-mt-2 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2">
                <hlm-icon name="lucideTriangleAlert" size="0.9rem" class="mt-0.5 flex-none text-warning" />
                <p class="text-sm text-foreground">
                  Need a <strong>permanent (no end date)</strong> discount? That requires the
                  <em>permanent-discount</em> permission — ask a team lead.
                </p>
              </div>
            }

            <div class="grid grid-cols-2 gap-4">
              <hlm-form-field>
                <label hlmLabel for="targetPrice">Target price</label>
                <input id="targetPrice" hlmInput type="number" formControlName="targetPrice"
                       [min]="0" [max]="maxPrice()" step="0.01" />
              </hlm-form-field>

              <hlm-form-field>
                <label hlmLabel for="discount">Discount (%)</label>
                <input id="discount" hlmInput type="number" formControlName="discount"
                       min="0" max="100" step="0.1" />
              </hlm-form-field>
            </div>

            <hlm-form-field>
              <label hlmLabel for="reason">Reason</label>
              <textarea id="reason" hlmTextarea formControlName="reason"
                        rows="3" maxlength="500"
                        [placeholder]="isPermanent() ? 'Why is this permanent discount granted?' : 'Why is this discount granted?'"></textarea>
              <p hlmFieldHint>Documented on the contract revision for audit. {{ form.value.reason?.length || 0 }} / 500</p>
            </hlm-form-field>
          </form>
        }
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()" [disabled]="submitting()">
          Cancel
        </button>
        <button hlmBtn (click)="submit()" [disabled]="!canSubmit()">
          @if (submitting()) {
            <hlm-spinner size="16" /> Applying...
          } @else {
            <hlm-icon name="lucidePercent" size="0.9rem" />
            Apply
          }
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class ChangeDiscountDialogComponent implements OnInit {
  readonly data = inject<ChangeDiscountDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<ChangeDiscountDialogComponent>);
  private readonly contractsService = inject(ContractsService);
  private readonly authService = inject(AuthService);
  private readonly permissionService = inject(PermissionService);
  private readonly sessionService = inject(SessionService);
  private readonly toast = inject(ToastService);
  private readonly fb = inject(FormBuilder);

  readonly loading = signal(false);
  readonly submitting = signal(false);
  readonly extensions = signal<BookedExtension[]>([]);
  readonly extensionNames = signal<Record<string, string>>({});
  readonly maxPrice = signal(this.data.monthlyFee ?? 0);
  readonly minDateStr = formatDate(new Date(), 'yyyy-MM-dd', 'en');

  /** Whether the operator may grant a permanent discount (server module). Session-stable. */
  readonly canGrantPermanent = this.permissionService.hasModule(PERMANENT_DISCOUNT_MODULE);

  /** Reactive flag: the currently selected duration is the permanent option. */
  readonly isPermanent = signal(false);

  private updatingPrice = false;

  /**
   * Fixed durations end on the last day the discount is active (inclusive): start + duration - 1 day,
   * replicating old CMT `calculateEndDate` (`endDate.subtract(1, 'days')`). Without the day off,
   * "1 month" from 09.10. ended 09.11. = 1 month + 1 day (HELP-584490).
   */
  private readonly boundedDurations: DurationOption[] = [
    { label: '1 week', addFn: (d) => addDays(d, 6) },
    { label: '2 weeks', addFn: (d) => addDays(d, 13) },
    { label: '3 weeks', addFn: (d) => addDays(d, 20) },
    { label: '1 month', addFn: (d) => addDays(addMonths(d, 1), -1) },
    { label: '2 months', addFn: (d) => addDays(addMonths(d, 2), -1) },
    { label: '3 months', addFn: (d) => addDays(addMonths(d, 3), -1) },
    { label: '4 months', addFn: (d) => addDays(addMonths(d, 4), -1) },
    { label: '5 months', addFn: (d) => addDays(addMonths(d, 5), -1) },
    { label: '6 months', addFn: (d) => addDays(addMonths(d, 6), -1) },
    // Special end-of-period options (after the fixed durations, before Permanent).
    { label: 'End of week', addFn: (d) => endOfWeek(d) },
    { label: 'End of billing period', addFn: (d) => endOfBillingPeriod(d, this.data.invoiceDay) },
  ];

  /** Bounded durations, plus the permanent option only when the operator holds the module. */
  readonly durations: DurationOption[] = this.canGrantPermanent
    ? [...this.boundedDurations, { label: 'Permanent (no end date)', permanent: true }]
    : this.boundedDurations;

  readonly form: FormGroup = this.fb.group({
    extensionIndex: [null],
    // Default to the next billing boundary (old-CMT parity); a past date is allowed (back-dating)
    // and only warned about, not blocked.
    startDate: [defaultDiscountStartDate(this.data.invoiceDay), [Validators.required, validStartDate]],
    durationIndex: [null, Validators.required],
    targetPrice: [this.data.monthlyFee ?? 0, [Validators.required, Validators.min(0)]],
    discount: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
    // Reason is mandatory for every discount (audit) — server persists it on the touched revisions.
    // nonBlankReason rejects whitespace-only input that would otherwise pass minLength.
    reason: ['', [Validators.required, nonBlankReason(3)]],
  });

  /**
   * Mirrors of the form controls the end-date hint depends on (the form itself is not a signal),
   * seeded from the controls so the hint can never start from a different default than the form.
   */
  private readonly startDateValue = signal<string>(this.form.controls['startDate'].value ?? '');
  private readonly durationIndexValue = signal<number>(
    this.form.controls['durationIndex'].value === null ? -1 : Number(this.form.controls['durationIndex'].value),
  );

  /** "Ends on dd.MM.yyyy (last discounted day)" for the chosen start + duration; null until both are set. */
  readonly endDateHint = computed(() => {
    const duration = this.durations[this.durationIndexValue()];
    if (!duration) return null;
    if (duration.permanent) return 'No end date.';
    const end = this.endDateFor(this.startDateValue(), this.durationIndexValue());
    return end ? `Ends on ${formatDate(end, 'dd.MM.yyyy', 'en')} (last discounted day).` : null;
  });

  /** Localized extension name (EB-4006): pricePackageName[labelKey] → name → labelKey. */
  extLabel(ext: BookedExtension): string {
    return extensionDualName(this.extensionNames(), ext.extensionPriceInfo.labelKey, ext.extensionPriceInfo.name);
  }

  /** Options for the searchable extension select: "Readable (technical) — price", indexed. */
  readonly extensionOptions = computed(() =>
    this.extensions().map((ext, idx) => ({
      value: idx,
      label: `${this.extLabel(ext)} — ${ext.extensionPriceInfo.monthlyFee} ${this.data.currency}`,
    })),
  );

  ngOnInit(): void {
    if (this.data.mode === 'extension') {
      this.loading.set(true);
      this.form.get('extensionIndex')!.setValidators(Validators.required);
      const today = formatDate(new Date(), 'yyyy-MM-dd', 'en');
      const brandId = this.sessionService.brandId() ?? 1;
      // Extension names must be resolved in the customer's language (EB-4143).
      const languageId = this.sessionService.customerLanguage() ?? 1;
      forkJoin({
        booked: this.contractsService.getBookedExtensions(this.data.contractId, today),
        names: this.contractsService.getExtensionNames(languageId, brandId).pipe(catchError(() => of({}))),
      }).subscribe({
        next: ({ booked, names }) => {
          this.extensionNames.set(names as Record<string, string>);
          // Sort by display name so the option index matches the shown order.
          this.extensions.set(
            [...booked].sort((a, b) => this.extLabel(a).localeCompare(this.extLabel(b))),
          );
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });

      this.form.get('extensionIndex')!.valueChanges.subscribe((idx: string | null) => {
        const index = idx !== null ? Number(idx) : -1;
        const ext = index >= 0 ? this.extensions()[index] ?? null : null;
        if (ext) {
          this.maxPrice.set(ext.extensionPriceInfo.monthlyFee);
          this.form.patchValue({ targetPrice: ext.extensionPriceInfo.monthlyFee, discount: 0 });
        }
      });
    }

    // Track whether the permanent duration is selected (drives endDate omission + routing).
    // Reason is mandatory regardless of duration, so no validator toggling here.
    this.form.get('durationIndex')!.valueChanges.subscribe((idx: string | null) => {
      const index = idx !== null ? Number(idx) : -1;
      this.isPermanent.set(index >= 0 && this.durations[index]?.permanent === true);
      this.durationIndexValue.set(index);
    });
    this.form.get('startDate')!.valueChanges.subscribe((value: string | null) => this.startDateValue.set(value ?? ''));

    // Bidirectional price <-> discount sync
    this.form.get('targetPrice')!.valueChanges.subscribe((price: number) => {
      if (this.updatingPrice) return;
      this.updatingPrice = true;
      const max = this.maxPrice();
      if (max > 0) {
        const disc = Math.round((1 - price / max) * 10000) / 100;
        this.form.patchValue({ discount: Math.max(0, Math.min(100, disc)) }, { emitEvent: false });
      }
      this.updatingPrice = false;
    });

    this.form.get('discount')!.valueChanges.subscribe((disc: number) => {
      if (this.updatingPrice) return;
      this.updatingPrice = true;
      const max = this.maxPrice();
      const price = Math.round(max * (1 - disc / 100) * 100) / 100;
      this.form.patchValue({ targetPrice: Math.max(0, price) }, { emitEvent: false });
      this.updatingPrice = false;
    });
  }

  /**
   * End date of a bounded duration, or null for a permanent/unknown duration or an unusable start date.
   * Local-midnight parse: `new Date('yyyy-MM-dd')` is UTC midnight, which shifts the booked end date one
   * day early in every timezone behind UTC (the arithmetic runs in local time).
   */
  private endDateFor(startDate: string, durationIndex: number): Date | null {
    const duration = this.durations[durationIndex];
    const start = parseApiDate(startDate);
    return duration?.addFn && start ? duration.addFn(start) : null;
  }

  canSubmit(): boolean {
    return this.form.valid && !this.loading() && !this.submitting();
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.submitting.set(true);

    const startDate = this.form.value.startDate;
    const durationIndex = Number(this.form.value.durationIndex);
    const permanent = this.durations[durationIndex]?.permanent === true;
    const discountValue = this.form.value.discount / 100;
    const employeeId = String(this.authService.currentUserId);
    const operations = [{ type: 'REPLACE', attribute: 'discount', value: discountValue }];

    // Reason is mandatory for every discount and is persisted on the touched revisions.
    // Both plan and extension discounts PATCH the contract API directly (modifyContractDirect /
    // modifyExtensionDiscount) so the reason param survives — bookingmgmt would drop it, and for a
    // bare discount change bookingmgmt is a pass-through anyway (it only cascades on a startDate patch).
    // Permanent grants omit endDate; bounded grants carry it.
    const params: Record<string, string> = { startDate, employeeId, reason: this.form.value.reason.trim() };
    if (!permanent) {
      const end = this.endDateFor(startDate, durationIndex);
      if (!end) {
        this.submitting.set(false);
        return;
      }
      params['endDate'] = formatDate(end, 'yyyy-MM-dd', 'en');
    }

    const extIdx = this.form.value.extensionIndex !== null ? Number(this.form.value.extensionIndex) : -1;
    const isExtension = this.data.mode === 'extension' && extIdx >= 0;

    const req$ = isExtension
      ? this.contractsService.modifyExtensionDiscount(
          this.data.contractId,
          this.extensions()[extIdx].extensionPriceInfo.name,
          operations,
          params,
        )
      : this.contractsService.modifyContractDirect(this.data.contractId, operations, params);

    req$.subscribe({
      next: () => {
        this.toast.open('Discount applied successfully', 'OK', { duration: 4000 });
        this.dialogRef.close(true);
      },
      error: (err: { status?: number }) => {
        // A permanent grant without the module is rejected server-side (defense in depth). Give a
        // clear message instead of a generic failure so the operator understands why.
        const message = err?.status === 403
          ? 'You are not allowed to grant a permanent discount. Please contact a team lead.'
          : 'Failed to apply discount';
        this.toast.open(message, 'OK', { duration: 6000 });
        this.submitting.set(false);
      },
    });
  }
}
